// T3-B PR 4-bis (2026-05-12):
//   motif_template_sets の create / edit。
//
// Spec: design/sponsored-motif-spec.md v3 §6.6 / §4.2
//       design/motif-future-roadmap-20260511.md v3 §8.1

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    templateSet: any | null,
    onSuccess?: () => void
}

const DEFAULT_TEMPLATE_SET_SLUG = 'currency-exchange-poc';

export default observer(function MotifTemplateSetFormSheet({ open, onOpenChange, mode, templateSet, onSuccess }: Props) {
    const {
        tierCatalogStore: {
            tierPresets,
            listTierPresets,
            createTemplateSet,
            updateTemplateSet
        }
    } = useStore();

    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [tierPresetId, setTierPresetId] = useState<string>('');
    const [supervisorRequired, setSupervisorRequired] = useState(false);
    const [supervisorInfo, setSupervisorInfo] = useState('');
    const [donationPct, setDonationPct] = useState<string>('0');
    const [isActive, setIsActive] = useState(true);
    const [displayOrder, setDisplayOrder] = useState<string>('0');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        // tier preset dropdown 用の load (= active のみ)
        flowResult(listTierPresets());
        if (mode === 'edit' && templateSet) {
            setName(templateSet.name ?? '');
            setSlug(templateSet.slug ?? '');
            setDescription(templateSet.description ?? '');
            setTierPresetId(templateSet.tier_preset_id ? String(templateSet.tier_preset_id) : '');
            setSupervisorRequired(!!templateSet.supervisor_required);
            setSupervisorInfo(templateSet.supervisor_info ?? '');
            setDonationPct(String(templateSet.donation_pct ?? 0));
            setIsActive(!!templateSet.is_active);
            setDisplayOrder(String(templateSet.display_order ?? 0));
        } else {
            setName('');
            setSlug('');
            setDescription('');
            setTierPresetId('');
            setSupervisorRequired(false);
            setSupervisorInfo('');
            setDonationPct('0');
            setIsActive(true);
            setDisplayOrder('0');
        }
    }, [open, mode, templateSet]);

    const isDefault = mode === 'edit' && templateSet?.slug === DEFAULT_TEMPLATE_SET_SLUG;

    const handleSubmit = async () => {
        if (!name.trim() || !slug.trim() || !tierPresetId) {
            toastify('error', '名称 / slug / tier preset は必須');
            return;
        }
        const payload: any = {
            name,
            slug,
            description: description || null,
            tier_preset_id: Number(tierPresetId),
            supervisor_required: supervisorRequired,
            supervisor_info: supervisorInfo || null,
            donation_pct: Number(donationPct) || 0,
            is_active: isActive,
            display_order: Number(displayOrder) || 0
        };
        setSubmitting(true);
        try {
            const res = mode === 'create' ?
                await flowResult(createTemplateSet(payload)) :
                await flowResult(updateTemplateSet({ id: templateSet.id, data: payload }));
            if (res?.ok) {
                toastify('success', mode === 'create' ? '作成しました' : '更新しました');
                onOpenChange(false);
                onSuccess?.();
            } else {
                toastify('error', res?.message || '保存に失敗しました');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[600px] sm:max-w-[600px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>{mode === 'create' ? 'テンプレートセット新規作成' : 'テンプレートセット編集'}</SheetTitle>
                    <SheetDescription>
                        運営が自由命名する motif の組。事業者は契約時に 1 セット選択。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    <div>
                        <Label>
                            名称
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input value={name} onChange={e => setName(e.target.value)} placeholder='例: 飲食 / 紅型 沖縄 PoC' />
                    </div>

                    <div>
                        <Label>
                            セットID(slug)
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            value={slug}
                            onChange={e => setSlug(e.target.value)}
                            placeholder='例: dining (小文字英数 + ハイフン)'
                            disabled={isDefault}
                        />
                        {isDefault && (
                            <p className='text-xs text-amber-700 mt-1'>
                                default テンプレ (
                                {DEFAULT_TEMPLATE_SET_SLUG}
                                ) は slug 変更不可
                            </p>
                        )}
                        <p className='text-xs text-gray-500 mt-1'>このセットを識別する英数字IDです(小文字英数+ハイフン、重複不可)。ランドマークに紐付けて使います。 例: okinawa-cafe-set</p>
                    </div>

                    <div>
                        <Label>
                            tier プリセット
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Select value={tierPresetId} onValueChange={setTierPresetId}>
                            <SelectTrigger>
                                <SelectValue placeholder='選択してください' />
                            </SelectTrigger>
                            <SelectContent>
                                {tierPresets.filter(p => p.is_active).map(p => (
                                    <SelectItem key={p.id} value={String(p.id)}>
                                        {p.name}
                                        {' '}
                                        (
                                        {p.preset_key}
                                        )
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label>説明</Label>
                        <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder='想定用途 / 業種 / 世界観' />
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type='checkbox'
                            checked={supervisorRequired}
                            onChange={e => setSupervisorRequired(e.target.checked)}
                            id='supervisor_required'
                        />
                        <Label htmlFor='supervisor_required'>supervisor_required (= 監修者承認必須、紅型 / 地域文化 用)</Label>
                    </div>

                    {supervisorRequired && (
                        <div>
                            <Label>supervisor_info</Label>
                            <Textarea value={supervisorInfo} onChange={e => setSupervisorInfo(e.target.value)} rows={2} placeholder='監修者の連絡先 / 承認プロセス' />
                        </div>
                    )}

                    <div className='grid grid-cols-2 gap-4'>
                        <div>
                            <Label>donation_pct (%)</Label>
                            <Input type='number' step='0.01' value={donationPct} onChange={e => setDonationPct(e.target.value)} />
                            <p className='text-xs text-gray-500 mt-1'>
                                文化保存基金への寄付率（この絵柄セットが【通常排出】で出た時に適用）。
                                ※ 現在は保存のみ・寄付集計は Phase 2 で対応予定。スポンサー排出は協賛キャンペーン側の寄付率を使用。
                            </p>
                        </div>
                        <div>
                            <Label>display_order</Label>
                            <Input type='number' value={displayOrder} onChange={e => setDisplayOrder(e.target.value)} />
                        </div>
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type='checkbox'
                            checked={isActive}
                            onChange={e => setIsActive(e.target.checked)}
                            id='ts_is_active'
                        />
                        <Label htmlFor='ts_is_active'>is_active (= 公開、事業者契約から選択可)</Label>
                    </div>
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        キャンセル
                    </Button>
                    <Button type='button' variant='active' onClick={handleSubmit} disabled={submitting}>
                        {mode === 'create' ? '作成' : '更新'}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});
